import React, { useState } from "react";
import { McButton } from "@maersk-global/mds-react-wrapper";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { toast } from "react-toastify";
import axios from "axios";

export default function DocumentsTab({ appName, channel = "red", documents, isLoading, onRefresh, userRole, userEmail }) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAdmin = userRole === "admin" || userRole === "change manager";

  const handleDownload = (doc) => {
    window.open(doc.downloadUrl, "_blank");
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/api/redChannelDocs/${encodeURIComponent(appName)}/${documentToDelete.id}?channel=${channel}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
          },
        }
      );
      toast.success("Document deleted successfully");
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
      onRefresh();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error(error.response?.data?.error || "Failed to delete document");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <mc-loading-indicator></mc-loading-indicator>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem",
          color: "#666",
          fontSize: "0.875rem",
        }}
      >
        <mc-icon icon="file-text" style={{ fontSize: "3rem", marginBottom: "1rem", opacity: 0.3 }}></mc-icon>
        <div>No documents uploaded</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "1rem" }}>
        {documents.map((doc) => (
          <div
            key={doc.id}
            style={{
              padding: "16px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              border: "1px solid #e0e0e0",
            }}
          >
            <mc-icon icon="file" style={{ fontSize: "1.5rem", color: "#0277bd" }}></mc-icon>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "600", fontSize: "0.875rem", marginBottom: "4px", color: "#141414" }}>
                {doc.fileName}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#666" }}>
                {formatFileSize(doc.sizeBytes)} • Uploaded {formatDate(doc.uploadedAt)}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <McButton
                fit="small"
                icon="tray-arrow-down"
                hiddenlabel={true}
                appearance="neutral"
                variant="outlined"
                click={() => handleDownload(doc)}
              />
              {isAdmin && (
                <McButton
                  fit="small"
                  icon="trash"
                  hiddenlabel={true}
                  appearance="neutral"
                  variant="outlined"
                  click={() => handleDeleteClick(doc)}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <McModal
        heading="Delete Document?"
        dimension="small"
        padding="default"
        open={deleteModalOpen}
        closed={handleCancelDelete}
        closing={handleCancelDelete}
      >
        <div style={{ fontSize: "0.875rem", color: "#666", lineHeight: "1.6" }}>
          Are you sure you want to delete <strong>{documentToDelete?.fileName}</strong>? This action cannot be undone.
        </div>
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          fit="medium"
          click={handleCancelDelete}
          disabled={isDeleting}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="error"
          fit="medium"
          click={handleConfirmDelete}
          disabled={isDeleting}
          loading={isDeleting}
        >
          Delete
        </McButton>
      </McModal>
    </>
  );
}
